/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { getSuggestionsOptionLabel } from '../../utils/hairstyleSuggestionsI18n';

// Brand accent color
const ACCENT = '#7d6633';

const HairstyleCard = ({
  id,
  name,
  overview,
  prompt,
  barberGuide,
  products,
  maintenance,
  tags,
  image,
  alt,
  gender
}) => {
  const { isDark } = useTheme();
  const { t } = useTranslation('admin');
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <motion.div 
      className="rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl cursor-pointer transition-all duration-200 hover:shadow-xl hover:scale-[1.02]"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Image Container - Fixed aspect ratio 4:5 */}
      <div className="aspect-[4/5] overflow-hidden rounded-t-2xl">
        <img
          src={image}
          alt={alt}
          className="w-full h-full object-cover transition-transform duration-200 hover:scale-105"
          loading="eager"
          decoding="async"
          fetchPriority="low"
          width={400}
          height={500}
          onError={(e) => {
            console.error('Failed to load hairstyle image:', image);
            e.target.style.display = 'none';
            // Show fallback text
            const fallbackDiv = document.createElement('div');
            fallbackDiv.className = 'w-full h-full flex items-center justify-center bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 text-sm';
            fallbackDiv.textContent = t('admin.suggestions_image_fallback', { name });
            e.target.parentNode.appendChild(fallbackDiv);
          }}
        />
      </div>

      {/* Content */}
      <div className="p-4 md:p-5">
        {/* Title */}
        <h3 className="text-lg font-semibold mb-2 text-zinc-900 dark:text-zinc-100">
          {name}
        </h3>

        {/* Overview */}
        <p className="text-sm mb-3 line-clamp-1 text-zinc-600 dark:text-zinc-400">
          {overview}
        </p>

        {/* Prompt preview */}
        {prompt && (
          <div className="mb-3">
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              <span className="font-medium text-zinc-700 dark:text-zinc-300">{t('admin.suggestions_prompt_label')}</span>{' '}
              <span className="line-clamp-1">{prompt}</span>
            </p>
          </div>
        )}

        {/* Tags */}
        {Array.isArray(tags) && tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs rounded-full bg-white/50 dark:bg-zinc-800/50 text-zinc-700 dark:text-zinc-300 border border-white/20 dark:border-white/10"
              >
                {getSuggestionsOptionLabel(tag, t)}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="px-2 py-1 text-xs rounded-full bg-white/50 dark:bg-zinc-800/50 text-zinc-700 dark:text-zinc-300 border border-white/20 dark:border-white/10">
                +{tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Details Accordion Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={toggleExpanded}
          className="w-full py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:ring-offset-2 bg-white/50 dark:bg-zinc-800/50 text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 border border-white/20 dark:border-white/10 focus:ring-offset-white dark:focus:ring-offset-zinc-900"
          style={{ '--accent': ACCENT }}
          aria-expanded={isExpanded}
          aria-controls={`details-${id}`}
        >
          <div className="flex items-center justify-between">
            <span>{t('admin.suggestions_details')}</span>
            <motion.svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </motion.svg>
          </div>
        </motion.button>

        {/* Accordion Content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              id={`details-${id}`}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="pt-4 space-y-4">
                {/* AI Prompt */}
                {prompt && (
                  <div className="p-3 rounded-xl bg-white/30 dark:bg-zinc-800/30 border border-white/20 dark:border-white/10">
                    <h4 className="text-sm font-medium mb-2 text-zinc-800 dark:text-zinc-200">
                      {t('admin.suggestions_ai_prompt')}
                    </h4>
                    <pre className="text-xs whitespace-pre-wrap break-words text-zinc-600 dark:text-zinc-400 leading-relaxed">
{prompt}
                    </pre>
                  </div>
                )}

                {/* Barber/Hairdresser Guide */}
                {barberGuide && (
                  <div className="p-3 rounded-xl bg-white/30 dark:bg-zinc-800/30 border border-white/20 dark:border-white/10">
                    <h4 className="text-sm font-medium mb-2 text-zinc-800 dark:text-zinc-200">
                      {gender === 'women'
                        ? t('admin.suggestions_hairdresser_guide')
                        : t('admin.suggestions_barber_guide')}
                    </h4>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      {barberGuide}
                    </p>
                  </div>
                )}

                {/* Products */}
                {products && (
                  <div className="p-3 rounded-xl bg-white/30 dark:bg-zinc-800/30 border border-white/20 dark:border-white/10">
                    <h4 className="text-sm font-medium mb-2 text-zinc-800 dark:text-zinc-200">
                      {t('admin.suggestions_products_finish')}
                    </h4>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      {products}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default HairstyleCard;
